import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Collapse,
	Divider,
	Grid,
	Stack,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DISPLAY_DATE_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import {
	useDeleteNewConsentMutation,
	useGetNewConsentDetailQuery,
} from '@/api/forms-management/newConsent';
import LogHistory from '../LogHistory';
import FormManagementChip from '@/common/components/FormManagementChip';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	useGetConsentPersonalDataUsageGenerateReportMutation,
	useGetExportCustomerPDFMutation,
} from '@/api/common';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function NewConsentDetailPage() {
	const { t } = useTranslation();
	const { preAdmissionId, newConsentId, admissionRequestId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const memberListType = location.pathname.includes('admission-request-list')
		? 'admission-request-list'
		: 'member-list';
	const [deleteNewConsent] = useDeleteNewConsentMutation();
	const showModal = useModal();

	const [getExportCustomerPDF] = useGetExportCustomerPDFMutation();
	const [getConsentPersonalDataUsageGenerateReport] =
		useGetConsentPersonalDataUsageGenerateReportMutation();

	const [applyExpanded, setApplyExpanded] = useState(true);
	const [witnessExpanded, setWitnessExpanded] = useState(true);
	const [consentExpanded, setConsentExpanded] = useState(true);

	const { data: details }: any = useGetNewConsentDetailQuery(
		newConsentId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const handleDelete = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={false}
				title={t('preAdmission.deleteConfirmation')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('preAdmission.isDeleteForm', {
							formName: t('preAdmission.newNewConsent'),
						})}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteNewConsent(details?.data.id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.enquiryRecordDeleteSuccess'), {
							variant: 'warning',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
						navigate(
							preAdmissionId
								? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
								: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`,
							{ replace: true }
						);
					}
				}}
			/>
		));
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.newConsentDetail')}
						<FormManagementChip
							sx={{ marginLeft: '10px' }}
							status={details?.data.formStatus}
							label={details?.data.formStatusDesc}
						/>
					</Typography>
					<Box>
						<Button
							endIcon={<FileDownloadOutlinedIcon />}
							onClick={async () => {
								const response =
									await getConsentPersonalDataUsageGenerateReport(
										details?.data.id
									);
								if ('data' in response) {
									getExportCustomerPDF(response?.data?.data.url);
								}
							}}
							variant='contained'
							sx={{ marginRight: '24px' }}
						>
							{t('button.exportAsPdf')}
						</Button>
						<Button
							endIcon={<DeleteOutlinedIcon />}
							onClick={() => handleDelete()}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Box>
				</Box>

				<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
					<Box display='flex' justifyContent='space-between' marginBottom={2}>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.zh_serviceDetails')}
						</Typography>
					</Box>
					<Grid container spacing={3} marginTop={0}>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.unit')}
								value={details?.data.branchName}
							/>
						</Grid>
					</Grid>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setApplyExpanded(!applyExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.applicantGuardianInfo')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: applyExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={applyExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.applicantName')}
									value={details?.data.nameZh}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.applicantHKID')}
									value={details?.data.hkid}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.applicationSignatureDate')}
									value={
										details?.data.applicationSignatureDate
											? dayjs(details?.data.applicationSignatureDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6} />
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.parentGuardianName')}
									value={details?.data.guardianName || ''}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.parentGuardianSignatureDateConsent')}
									value={
										details?.data.guardianSignatureDate
											? dayjs(details?.data.guardianSignatureDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setWitnessExpanded(!witnessExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.witnessInformation')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: witnessExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={witnessExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.witnessId')}
									value={details?.data.witnessName}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.witnessPosition')}
									value={details?.data.witnessPosition}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.witnessDate')}
									value={
										details?.data.witnessSignatureDate
											? dayjs(details?.data.witnessSignatureDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setConsentExpanded(!consentExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.consentForm')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: consentExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={consentExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.postalPreference')}
									value={
										details?.data.mailRequest
											? t('preAdmission.isConsentFormTitle')
											: t('preAdmission.noConsentFormTitle')
									}
								/>
							</Grid>
						</Grid>
					</Collapse>
				</Card>

				{details?.data.id && (
					<Card sx={{ margin: '24px 0' }}>
						<CardContent>
							<LogHistory formId={details.data.id} />
						</CardContent>
					</Card>
				)}
			</Box>
		</>
	);
}
